import React from "react";
import {
  courseChatService,
  type CourseChatBootstrapResponse,
  type CourseChatMessage,
  type CourseChatScope,
  type CourseChatSettings,
} from "services/courseChatService";
import { ensureFirebaseSignedIn } from "services/firebase/firebaseAuth";
import { isFirebaseConfigured } from "services/firebase/firebaseClient";
import { listenToCourseChat } from "services/firebase/firestoreChat";

export type FirebaseConnectionMode = "connecting" | "live" | "error";

type Handlers = {
  onMessages?: (messages: CourseChatMessage[]) => void;
  onSettings?: (settings: CourseChatSettings) => void;
  onBootstrap?: (bootstrap: CourseChatBootstrapResponse) => void;
  onIdentity?: (info: { firebaseUid: string; appUserId: number }) => void;
};

export function useFirebaseCourseChat(
  courseId: number | string | undefined,
  scope: CourseChatScope,
  handlers: Handlers,
  enabled = true
): { connectionMode: FirebaseConnectionMode } {
  const handlersRef = React.useRef(handlers);
  handlersRef.current = handlers;
  const [connectionMode, setConnectionMode] = React.useState<FirebaseConnectionMode>("connecting");

  React.useEffect(() => {
    if (!enabled || courseId == null || courseId === "" || !isFirebaseConfigured()) {
      setConnectionMode("error");
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    const start = async () => {
      try {
        setConnectionMode("connecting");
        const bootstrap = await courseChatService.bootstrap(scope, courseId);
        // Single sign-in returns the verified identity/claims (no extra token call).
        const session = await ensureFirebaseSignedIn();
        if (cancelled) return;

        handlersRef.current.onBootstrap?.(bootstrap);

        const viewerAppUserId = session.appUserId;
        handlersRef.current.onIdentity?.({
          firebaseUid: bootstrap.firebase?.uid ?? session.firebaseUid,
          appUserId: viewerAppUserId,
        });

        unsubscribe = listenToCourseChat(
          bootstrap,
          viewerAppUserId,
          {
            onMessages: (messages) => handlersRef.current.onMessages?.(messages),
            onSettings: (settings) => handlersRef.current.onSettings?.(settings),
            onError: () => {
              if (!cancelled) setConnectionMode("error");
            },
          }
        );

        if (!cancelled) setConnectionMode("live");
      } catch {
        if (!cancelled) setConnectionMode("error");
      }
    };

    void start();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [courseId, scope, enabled]);

  return { connectionMode };
}
